import React, { useState, useEffect, useRef, useCallback, useId } from 'react';
// eslint-disable-next-line no-unused-vars
import { motion, AnimatePresence } from 'framer-motion';
import {
    Upload, FileText, Trash2, RefreshCw,
    CheckCircle, AlertCircle, Loader2
} from 'lucide-react';
import { profileDocuments } from '../services/api';

/**
 * ContextFileUploader — drag-and-drop + file-picker for general profile documents.
 *
 * Uploads through /upload-document (profileDocuments.upload),
 * lists via /get-profile-docs (profileDocuments.list),
 * deletes via /delete-profile-doc (profileDocuments.delete).
 *
 * Does NOT use /api/upload-curriculum (that is for structured curriculum extraction).
 */

const ACCEPTED_TYPES = '.pdf,.doc,.docx,.txt,.csv';

const ContextFileUploader = () => {
    const [docs, setDocs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pendingFiles, setPendingFiles] = useState([]); // { id, name, status: 'uploading'|'success'|'error', error? }
    const [dragOver, setDragOver] = useState(false);
    const fileInputRef = useRef(null);
    const fileInputId = useId();

    // ── Load saved documents ──────────────────────────────────────────
    const refreshDocs = useCallback(async () => {
        try {
            const res = await profileDocuments.list();
            setDocs(res.data?.documents || []);
        } catch (err) {
            console.error('[ContextFileUploader] Failed to load docs:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { refreshDocs(); }, [refreshDocs]);

    // ── Canonical upload handler (shared by drop + file picker) ──────
    const uploadContextFiles = useCallback(async (fileList) => {
        const files = Array.from(fileList);
        if (!files.length) return;

        // Show pending rows immediately
        const newPending = files.map((f, i) => ({
            id: `pending-${Date.now()}-${i}`,
            name: f.name,
            status: 'uploading',
        }));
        setPendingFiles(prev => [...prev, ...newPending]);

        // Upload each file
        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            const pendingId = newPending[i].id;
            try {
                await profileDocuments.upload(file);
                setPendingFiles(prev =>
                    prev.map(p => p.id === pendingId ? { ...p, status: 'success' } : p)
                );
            } catch (err) {
                console.error(`[ContextFileUploader] Upload failed for ${file.name}:`, err);
                const detail = err.response?.data?.detail || err.message || 'Upload failed';
                setPendingFiles(prev =>
                    prev.map(p => p.id === pendingId ? { ...p, status: 'error', error: detail } : p)
                );
            }
        }

        // Refresh the persisted list from backend
        await refreshDocs();

        // Clear successful pending rows after a short delay
        setTimeout(() => {
            setPendingFiles(prev => prev.filter(p => p.status === 'error'));
        }, 2000);
    }, [refreshDocs]);

    // ── Drag handlers ────────────────────────────────────────────────
    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOver(true);
    };
    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOver(false);
    };
    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOver(false);
        if (e.dataTransfer?.files?.length) {
            uploadContextFiles(e.dataTransfer.files);
        }
    };

    // ── File picker handler ──────────────────────────────────────────
    const handleFileChange = (e) => {
        const selected = e.target.files;
        if (selected?.length) {
            uploadContextFiles(selected);
        }
        // Reset input so selecting the same file again triggers onChange
        e.target.value = '';
    };

    // ── Delete handler ───────────────────────────────────────────────
    const handleDelete = async (docId) => {
        if (!window.confirm('Remove this document?')) return;
        try {
            await profileDocuments.delete(docId);
            await refreshDocs();
        } catch (err) {
            console.error('[ContextFileUploader] Delete failed:', err);
        }
    };

    return (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            {/* Header */}
            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                    <FileText size={14} className="text-amber-500" />
                    General Saige Context
                </h4>
                <span className="text-[10px] text-slate-400">
                    Documents that inform all Saige interactions
                </span>
            </div>

            {/* Drop zone + upload button */}
            <div
                className={`m-3 rounded-xl border-2 border-dashed transition-all ${
                    dragOver
                        ? 'border-amber-400 bg-amber-50'
                        : 'border-slate-200 hover:border-amber-300'
                }`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
            >
                <div className="flex flex-col items-center py-5 gap-2">
                    <Upload size={20} className={dragOver ? 'text-amber-500' : 'text-slate-300'} />
                    <p className="text-xs text-slate-500">
                        Drag &amp; drop files here
                    </p>
                    <label
                        htmlFor={fileInputId}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-500 text-white text-xs font-medium rounded-lg cursor-pointer hover:bg-amber-600 transition-colors"
                    >
                        <Upload size={12} />
                        Upload files
                    </label>
                    <input
                        ref={fileInputRef}
                        id={fileInputId}
                        type="file"
                        multiple
                        accept={ACCEPTED_TYPES}
                        className="hidden"
                        onChange={handleFileChange}
                    />
                    <p className="text-[10px] text-slate-400">
                        PDF, Word, TXT, CSV
                    </p>
                </div>
            </div>

            {/* Pending files */}
            <AnimatePresence>
                {pendingFiles.map(pf => (
                    <motion.div
                        key={pf.id}
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="mx-3 mb-1"
                    >
                        <div className={`flex items-center gap-2 p-2.5 rounded-lg border ${
                            pf.status === 'uploading' ? 'bg-amber-50 border-amber-200' :
                            pf.status === 'success' ? 'bg-emerald-50 border-emerald-200' :
                            'bg-red-50 border-red-200'
                        }`}>
                            {pf.status === 'uploading' && <Loader2 size={14} className="animate-spin text-amber-500 flex-shrink-0" />}
                            {pf.status === 'success' && <CheckCircle size={14} className="text-emerald-500 flex-shrink-0" />}
                            {pf.status === 'error' && <AlertCircle size={14} className="text-red-500 flex-shrink-0" />}
                            <div className="flex-1 min-w-0">
                                <p className="text-xs font-medium text-slate-700 truncate">{pf.name}</p>
                                {pf.status === 'uploading' && <p className="text-[10px] text-amber-600">Uploading…</p>}
                                {pf.status === 'success' && <p className="text-[10px] text-emerald-600">Saved</p>}
                                {pf.status === 'error' && <p className="text-[10px] text-red-600">{pf.error}</p>}
                            </div>
                        </div>
                    </motion.div>
                ))}
            </AnimatePresence>

            {/* Saved documents list */}
            <div className="px-3 pb-3">
                {loading ? (
                    <div className="flex justify-center py-4">
                        <RefreshCw size={16} className="animate-spin text-slate-300" />
                    </div>
                ) : docs.length === 0 && pendingFiles.length === 0 ? (
                    <div className="text-center py-4 bg-slate-50 rounded-lg">
                        <p className="text-xs text-slate-400">No documents uploaded yet</p>
                    </div>
                ) : (
                    <div className="space-y-1">
                        {docs.map(doc => (
                            <div
                                key={doc.id}
                                className="flex items-center gap-2 p-2.5 rounded-lg bg-slate-50 border border-slate-100 group hover:border-slate-200 transition-colors"
                            >
                                <FileText size={14} className="text-slate-400 flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                    <p className="text-xs font-medium text-slate-700 truncate">
                                        {doc.name || 'Untitled'}
                                    </p>
                                    <div className="flex items-center gap-2 mt-0.5">
                                        {doc.type && (
                                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-600">
                                                {doc.type}
                                            </span>
                                        )}
                                        {doc.summary && (
                                            <span className="text-[10px] text-slate-400 truncate">
                                                {doc.summary}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <button
                                    onClick={() => handleDelete(doc.id)}
                                    className="opacity-0 group-hover:opacity-100 p-1 text-slate-400 hover:text-red-500 transition-all"
                                    title="Remove"
                                >
                                    <Trash2 size={12} />
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ContextFileUploader;
